// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import { Typography, IconButton } from '@material-ui/core';
import { _isToday } from 'utils/dateUtils';
import { ORDER_SERVICES_DEPT_TYPE, ORDER_CONFIRMATION_TYPE, STORE_HOURS, LOYALTY_CARD_TYPES } from 'constants/index';
import { useBooleanState } from 'modules/shell/hooks';
import { Preloader, MainHeader, PageLayout, OrderConfirmation, InventoryDrawer } from 'sharedComponents';
import { ReceiptIcon } from 'sharedComponents/Icons';
import { OrderPropTypes } from 'utils/PropTypesValues';
import { formatOrderForGiftReceipt } from 'utils';
import VoidConfirmation from './VoidConfirmation';
import OrderDetails from '../../components/OrderDetails';
import SendsReceiptModal from '../../../cart/components/SendsReceiptModal';
import { mapVoidOrderPricing, mapOrderPricing, getGroupedServicesByDept, validateStoreHours } from '../../utils';

const OrderDetailsPage = ({
  loading,
  error,
  data,
  confirmationData,
  resetOrderDetails,
  fetchData,
  userBrand,
  orderId,
  isFailedOrder,
  isVoidForOrderDetailsEnabled,
  scrollAreaRef,
  saveScrollPosition,
  addCachedOrderData,
  voidOrder,
  sendGiftReceipt,
  restoreScrollCache,
  channel,
  isDeliveryReceiptCopyEnabled,
  selectedStoreId,
  isVertexOEnabled,
}) => {
  const [isConfirmOpen, openConfirm, closeConfirm] = useBooleanState();
  const [isReceiptModalOpen, openReceiptModal, closeReceiptModal] = useBooleanState();
  const [isInventoryDrawerOpen, openInventoryDrawer, closeInventoryDrawer] = useBooleanState();
  const [isLoading, setLoading] = React.useState(false);
  const [giftReceiptData, setGiftReceiptData] = React.useState({});

  const { orderDetails, paymentDetails } = data || {};
  const { shippingAddress, items } = !_isEmpty(orderDetails) ? orderDetails[0] : { shippingAddress: {}, items: [] };
  const cachedOrderData = restoreScrollCache || {};
  const hasCachedOrder = !_isEmpty(restoreScrollCache);
  const orderServices = Object.keys(ORDER_SERVICES_DEPT_TYPE);
  const clientData = {
    firstName: data?.soldTo?.firstName,
    lastName: data?.soldTo?.lastName,
    email: data?.soldTo?.email,
  };

  const [isStoreSendsOrder, isStoreTakesOrder] = React.useMemo(() => {
    const containsSends = items && !!items.find((product) => product.transactionType === 'SEND');
    const containsTakes = items && !!items.find((product) => product.transactionType === 'TAKE');

    return [containsSends, containsTakes];
  }, [items]);

  // filtering all Alteration, Gift Wrap & Delivery fees from items
  const filteredItems = React.useMemo(() => {
    return items?.filter((item) => !orderServices.includes(item.dept) && !item.cmosSkuId?.includes('OSP'));
  }, [items]);

  const groupedServicesList = Object.values(getGroupedServicesByDept(items));

  const filteredServices = React.useMemo(() => {
    return groupedServicesList?.filter((item) => orderServices.includes(item.service));
  }, [groupedServicesList]);

  const isMounted = React.useRef(false);

  /**
   * Acceptance Criteria:
   * @see https://neimanmarcus.atlassian.net/browse/OSP-10971
   * Overrided by below ticket
   * @see https://neimanmarcus.atlassian.net/browse/OSP-14796
   * TO-DO: STORE_HOURS will be fetched from BE later on. The below is temprory fix, we need to update it once the store hour service is available.
   */
  const storeInfo = STORE_HOURS.get(Number(selectedStoreId));
  let isOrderRungToday = false;
  if (storeInfo) {
    const isStoreOpen = validateStoreHours(storeInfo);
    isOrderRungToday = _isToday(data?.orderDate, storeInfo.timeZone) && isStoreOpen;
  }

  const onVoidConfirm = async () => {
    try {
      await voidOrder(orderId, isVertexOEnabled);
    } catch (error) {
      return '';
    } finally {
      closeConfirm();
    }
  };

  const onSendGiftReceipt = async (selectedItems) => {
    const payload = formatOrderForGiftReceipt(data, selectedItems);

    setGiftReceiptData(payload);
    await sendGiftReceipt(payload);
  };

  const headerTitle = isFailedOrder ? 'Order Failed' : 'Order Details';

  React.useEffect(() => {
    if (hasCachedOrder) {
      addCachedOrderData(cachedOrderData);
    } else {
      fetchData(orderId, channel);
    }

    return () => {
      resetOrderDetails();
    };
  }, []);

  React.useEffect(() => {
    if (isMounted.current) {
      fetchData(orderId, channel);
    }
    isMounted.current = true;
  }, [orderId, channel]);

  /**
   * TODO: clean this up refactor
   */
  if (!_isEmpty(confirmationData)) {
    const { type, voidOrderId, storeAddressInfo } = confirmationData;
    const isVoid = type === ORDER_CONFIRMATION_TYPE.VOID;
    const isGift = type === ORDER_CONFIRMATION_TYPE.GIFT;
    const mappedShipmentData = isVoid ? mapVoidOrderPricing(data, filteredServices) : mapOrderPricing(data);
    const paymentsExcludingGC = data.additionalPaymentDetails
      ? data.additionalPaymentDetails.filter((e) => e.type !== LOYALTY_CARD_TYPES.NEX)
      : null;
    // @ts-ignore
    const orderDetailsGift = giftReceiptData?.orderData?.orderDetails[0]?.items;

    return (
      <OrderConfirmation
        clientData={clientData}
        // TODO: for return case we need return order id
        orderId={isVoid ? voidOrderId : orderId}
        // TODO: for return case we have custom list of items
        productsData={isGift ? orderDetailsGift : filteredItems}
        shippingAddress={shippingAddress}
        // TODO: for return case we need receive from BE pricing data of return order
        mappedShipmentData={mappedShipmentData}
        confirmationType={type}
        selectedPayments={paymentsExcludingGC ? [paymentDetails, ...paymentsExcludingGC] : [paymentDetails]}
        storeAddressInfo={storeAddressInfo}
        confirmationData={confirmationData}
      />
    );
  }

  return (
    <>
      <MainHeader title={headerTitle} closeIcon="back" divider>
        {isStoreSendsOrder && isDeliveryReceiptCopyEnabled && (
          <IconButton aria-label="sends-receipt-icon" onClick={openReceiptModal}>
            <ReceiptIcon />
          </IconButton>
        )}
      </MainHeader>
      <PageLayout padding="0" ref={scrollAreaRef}>
        {(loading || isLoading) && <Preloader usePortal />}
        {error && <Typography variant="h3">Order details are not available</Typography>}
        {!error && data && (
          <OrderDetails
            order={data}
            filteredItems={filteredItems}
            groupedServicesList={groupedServicesList}
            userBrand={userBrand}
            isOrderRungToday={isOrderRungToday}
            isFailedOrder={isFailedOrder}
            isVoidForOrderDetailsEnabled={isVoidForOrderDetailsEnabled}
            openVoidConfirm={openConfirm}
            openInventoryDrawer={openInventoryDrawer}
            saveScrollPosition={saveScrollPosition}
            isSendsTakesOrder={isStoreSendsOrder || isStoreTakesOrder}
            selectedStoreId={selectedStoreId}
          />
        )}
        <VoidConfirmation open={isConfirmOpen} onClose={closeConfirm} onConfirm={onVoidConfirm} />
        {data && (
          <InventoryDrawer
            data={data}
            onClose={closeInventoryDrawer}
            open={isInventoryDrawerOpen}
            title="Send Gift Receipt"
            submitBtnLabel="Send Gift Receipt"
            onSubmitBtnClick={onSendGiftReceipt}
            selectAllCheckboxAvailable
            separateEachItem
          />
        )}
        <SendsReceiptModal
          onClose={closeReceiptModal}
          isOpen={isReceiptModalOpen}
          orderId={orderId}
          setLoadingState={setLoading}
          setModalClosed={closeReceiptModal}
        />
      </PageLayout>
    </>
  );
};

OrderDetailsPage.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.bool,
  fetchData: PropTypes.func,
  resetOrderDetails: PropTypes.func,
  data: OrderPropTypes,
  confirmationData: PropTypes.shape({}),
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  isFailedOrder: PropTypes.bool,
  isVoidForOrderDetailsEnabled: PropTypes.bool,
  channel: PropTypes.string,
  isVertexOEnabled: PropTypes.bool,
  selectedStoreId: PropTypes.string,
};

OrderDetailsPage.defaultProps = {
  error: false,
  loading: false,
  isFailedOrder: false,
  confirmationData: {},
  isVoidForOrderDetailsEnabled: false,
  selectedStoreId: '',
  isVertexOEnabled: false,
};

export default OrderDetailsPage;
